/**
 * 役割と「できること」の表（PRODUCT_DEV-721 設計レビュー指摘3・5）
 *
 * 権限は2つの軸で持つ（9/8 プロダクト要件会議）。
 *
 * | 軸 | 役割（弱い → 強い） | どこに持つか |
 * |---|---|---|
 * | システム軸 | general → power → admin → sysadmin | `DDBUserTenant.role` ＋ `specialFlags` |
 * | 業務軸 | member → manager | 所属レコードの `deptRole`（部署ごと） |
 *
 * **強い役割は弱い役割のできることをすべて持つ**（指摘5: member ⊂ manager、power ⊂ admin ⊂ sysadmin）。
 *
 * 画面での呼び名（PRODUCT_DEV-1235・2026-10-29 決定）: general＝一般ユーザー（閲覧者・メンバー）、
 * power＝パワーユーザー（推進・事務局）、admin＝管理ユーザー（システムの管理）、sysadmin＝システム管理者（Umeee だけ）。
 * そのため表は「このことに**最低限**どの役割が要るか」で書く。
 *
 * ⚠ **役割を足すときは `*_ROLE_LADDER` に1つ挟むだけ**、できることを足すときは
 * `*_CAPABILITY_MIN_ROLE` に1行足すだけで済むようにしてある（指摘3）。
 * 判定を各所で `role === "admin"` と書かないこと。書くと役割が増えたときに漏れる。
 *
 * api（`apps/unizo/api/lib/permissions`）と web（`apps/unizo/web/src/utils/role.ts`）の
 * **両方がこの表を読む**。画面の出し分けとサーバの認可で表がずれないようにするため。
 * ⚠ web 側は表示の出し分けだけ（改ざんは許容）。認可の正はサーバ。
 */

import { SPECIAL_FLAGS } from "../lib/specialFlags/registry";

// ---- システム軸 ----

/**
 * システム軸の役割。弱い順。`general` は `role` が member / viewer の人、`power` は `role` が power の人
 * （PRODUCT_DEV-1235）。
 */
export const SYSTEM_ROLE_LADDER = ["general", "power", "admin", "sysadmin"] as const;
export type SystemRole = (typeof SYSTEM_ROLE_LADDER)[number];

/** `specialFlags` に入る、sysadmin を表す値（正本は特殊権限のレジストリ）。 */
export const SYSADMIN_SPECIAL_FLAG = SPECIAL_FLAGS.sysadmin.key;

/** システム軸でできること。 */
export type SystemCapability =
  /** 管理メニュー（管理者トップ）に入る */
  | "admin.enter"
  /** 議事録・メモ・ToDo をテナント全件見る、他人の商談の文字起こしを見る */
  | "records.readAll"
  /** 他人の商談・議事録・商談グループ・レポートを代わりに編集・削除する */
  | "records.editOthers"
  /** 商談・商談グループ・顧客を完全に削除する */
  | "records.hardDelete"
  /**
   * 顧客マスタ（CSV 取込・レポート生成）・部署を管理する。テナント辞書では**他の人が足した言葉**を
   * 変更する（見る・足す・自分が足した言葉の変更は誰でも。`canModifyDictionaryEntry`・PRODUCT_DEV-1108）
   */
  | "tenantData.manage"
  /** 配信の設定（締め日・下書きの時刻・提出期限・メール配信）を変える */
  | "delivery.manage"
  /** ユーザー管理・パスワード再設定 */
  | "users.manage"
  /** データ出力（1回の出力の上限 EXPORT_MAX_ROWS まで） */
  | "data.export"
  /** データ出力で1回の上限を超えて、少しずつ取って出す（PRODUCT_DEV-1237）。いまは data.export と同じ段 */
  | "data.exportAll"
  /** プリセットの管理（公開状態の変更を含む） */
  | "presets.manage"
  /**
   * テナント設定のうち本社都道府県・初期表示ページ（PRODUCT_DEV-1093・PRODUCT_DEV-1235 でパワーユーザーへ）
   */
  | "tenantSettings.general"
  /**
   * セキュリティ設定（MFA・パスキー・IP 制限・外部連携）。PRODUCT_DEV-1235 で `tenantSettings.general` から分けた
   */
  | "tenantSettings.security"
  /** テナント設定のうちログイン画面の表示・Webhook 通知と、リアルタイム API 設定 */
  | "tenantSettings.manage"
  /** 特殊権限の付与と、sysadmin 本人の権限・状態・連絡先を変える */
  | "sysadmins.manage";

/** できることごとに、最低限要る役割。 */
export const SYSTEM_CAPABILITY_MIN_ROLE: Record<SystemCapability, SystemRole> = {
  // PRODUCT_DEV-1235: パワーユーザー（推進・事務局）に開くもの。記録を見る・整える・抜き出す・業務のデータ
  "admin.enter": "power",
  "records.readAll": "power",
  "records.editOthers": "power",
  "tenantData.manage": "power",
  "delivery.manage": "power",
  "data.export": "power",
  // 10/30 日暮さん: 上限を超える出力もパワーユーザーに開く（「全量」は監査ログの水準で、データ出力とは別・PD-1299）
  "data.exportAll": "power",
  "tenantSettings.general": "power",
  // 管理ユーザー（システムの管理）だけに残すもの。戻せない削除・アカウント・認証と接続
  "records.hardDelete": "admin",
  "users.manage": "admin",
  "tenantSettings.security": "admin",
  "presets.manage": "sysadmin",
  // PRODUCT_DEV-1093: テナント設定はログイン画面の表示・Webhook だけを sysadmin に残した
  "tenantSettings.manage": "sysadmin",
  "sysadmins.manage": "sysadmin",
};

/** システム軸の判定に要るぶん。`DDBUserTenant` やメンバーシップをそのまま渡せる。 */
export interface SystemRoleSource {
  role: string;
  specialFlags?: readonly string[] | null;
}

/**
 * `role` と `specialFlags` からシステム軸の役割を決める。sysadmin は admin であることが前提。
 *
 * ⚠ 返り値を `=== "general"` / `!== "general"` で比べて「管理者か」を決めないこと（power が混ざる）。
 * 段で比べるときは `isSystemRoleAtLeast` を使う（PRODUCT_DEV-1235 事前レビュー）。
 */
export const resolveSystemRole = (source: SystemRoleSource): SystemRole => {
  if (source.role === "power") return "power";
  if (source.role !== "admin") return "general";
  return (source.specialFlags ?? []).includes(SYSADMIN_SPECIAL_FLAG)
    ? "sysadmin"
    : "admin";
};

/**
 * 所属行に sysadmin フラグがあるか（**role を問わない**）。
 *
 * ⚠ 役割の判定（`resolveSystemRole`）は admin を前提にするが、**守る側の判定はフラグだけで見る**。
 * sysadmin を member に下げてもフラグは残るので、role で見ると「admin がその人を admin に戻した
 * 瞬間に sysadmin が生まれる」経路を塞げない。
 */
export const hasSysadminFlag = (source: {
  specialFlags?: readonly string[] | null;
}): boolean => (source.specialFlags ?? []).includes(SYSADMIN_SPECIAL_FLAG);

/**
 * ユーザー管理の操作が、どこまで効くか。
 * - `tenant` … そのテナントの所属行だけ（権限・無効化・有効化・そのテナントからの削除）
 * - `account` … 全テナントで共有しているアカウント（連絡先・パスワード・MFA・名前・エイリアス）
 */
export type UserOperationScope = "tenant" | "account";

/**
 * **sysadmin にしか操作させない相手か**（PRODUCT_DEV-721 設計レビュー・RULES Rule 5）。
 *
 * ユーザー管理を admin へ降ろしたので、admin が次のことをできないようにする。
 * - そのテナントで sysadmin の人を下げる・無効化・削除する（`tenant`）
 * - **アカウントそのもの**を書き換えて乗っ取る（`account`）。アカウントは全テナント共有なので、
 *   **どこかのテナントで sysadmin の人**と、**ほかのテナントにも所属している人**は守る。
 *   前者は「別テナントの sysadmin を自分のテナントへ紐付けてから連絡先を変える」、
 *   後者は「別の顧客の管理者のパスワードを再設定する」というテナント越えの乗っ取りになる。
 */
export const isProtectedTargetUser = (
  target: {
    currentTenantId: string;
    memberships: readonly {
      tenantId: string;
      specialFlags?: readonly string[] | null;
    }[];
  },
  scope: UserOperationScope,
): boolean => {
  if (scope === "tenant") {
    return target.memberships.some(
      (m) => m.tenantId === target.currentTenantId && hasSysadminFlag(m),
    );
  }
  return target.memberships.some(
    (m) => hasSysadminFlag(m) || m.tenantId !== target.currentTenantId,
  );
};

const atLeast = <R extends string>(
  ladder: readonly R[],
  role: R,
  min: R,
): boolean => ladder.indexOf(role) >= ladder.indexOf(min);

/** システム軸の役割が、少なくとも `min` 以上か（メニューを役割の段で分けて見せるときに使う）。 */
export const isSystemRoleAtLeast = (
  source: SystemRoleSource,
  min: SystemRole,
): boolean => atLeast(SYSTEM_ROLE_LADDER, resolveSystemRole(source), min);

/** そのことに最低限要る役割。メニューの権限バッジや API の認証ミドルウェアの役割指定にも使う。 */
export const minimumSystemRoleFor = (capability: SystemCapability): SystemRole =>
  SYSTEM_CAPABILITY_MIN_ROLE[capability];

/** システム軸でそのことができるか。 */
export const hasSystemCapability = (
  source: SystemRoleSource,
  capability: SystemCapability,
): boolean =>
  atLeast(SYSTEM_ROLE_LADDER, resolveSystemRole(source), minimumSystemRoleFor(capability));

// ---- 業務軸 ----

/** 業務軸の役割。弱い順。`contract.ts` の `DeptRoleSchema` と同じ値。 */
export const DEPT_ROLE_LADDER = ["member", "manager"] as const;
export type DeptRoleRank = (typeof DEPT_ROLE_LADDER)[number];

/** 業務軸でできること（部署ごと）。 */
export type DeptCapability =
  /** 部のまとめ・音声を見る、部下の日報を読む・コメントする */
  | "team.view"
  /** 部のまとめメールを受け取る */
  | "team.receiveMail"
  /**
   * その部署の配信の設定（締め日・下書きの時刻・提出期限・メール配信・使う種類）を変える（PRODUCT_DEV-721 U7）。
   */
  | "team.manageDelivery";

/** できることごとに、最低限要る業務軸の役割。 */
export const DEPT_CAPABILITY_MIN_ROLE: Record<DeptCapability, DeptRoleRank> = {
  "team.view": "manager",
  "team.receiveMail": "manager",
  "team.manageDelivery": "manager",
};

/** その部署での役割で、そのことができるか。 */
export const deptRoleHasCapability = (
  deptRole: string,
  capability: DeptCapability,
): boolean => {
  if (!(DEPT_ROLE_LADDER as readonly string[]).includes(deptRole)) return false;
  return atLeast(
    DEPT_ROLE_LADDER,
    deptRole as DeptRoleRank,
    DEPT_CAPABILITY_MIN_ROLE[capability],
  );
};

// ---- 商談の変更（PRODUCT_DEV-721 第3期 U5） ----
// api（`apps/unizo/api/lib/permissions` が re-export）と web（`lib/meetings/meetingPermission.ts`）が同じ判定を使う。

/** 商談の判定に要る部署の情報。本人の所属は**削除されていない部署だけ**。 */
export interface ViewerDepartments {
  /**
   * テナントの削除されていない部署。**null は「分からない」**（画面はテナントの部署一覧を持たない）で、
   * そのときは商談の部署を生きている部署として扱う（削除済みの部署の商談を作成者に開かない＝締める側）
   */
  liveDeptIds: ReadonlySet<string> | null;
  /** 本人が所属している、削除されていない部署 */
  viewerDeptIds: readonly string[];
}

/** 商談を変更する人。api は `loadMeetingActor`（apps/unizo/api/lib/meetings/meetingAccess.ts）で組む。 */
export interface MeetingActor {
  userId: string;
  /** `records.editOthers`（admin 以上）。持っていれば部署を問わず変更できる */
  canEditOthers: boolean;
  /** admin でないときだけ読む（admin は null） */
  departments: ViewerDepartments | null;
}

/**
 * 商談を変更（編集・振り返りの追記・日程調整のつなぎ替え・名称変更・ゴミ箱へ移動・復元・部署の付け替え）
 * できるか（PRODUCT_DEV-721 第3期 U5・9/15 設計レビュー指摘9）。
 *
 * - admin 以上（`records.editOthers`）は従来どおり誰の商談でも変更できる
 * - それ以外は作成者だけで、さらに**商談の部署に所属していること**
 *   （異動して所属が外れた作成者は変更できない。2026-09-17 ユーザ判断）
 * - 商談に部署が無い（U5 より前の記録・所属の無い人の記録）か、その部署が削除済みなら作成者だけで判定する
 *
 * ⚠ 部署を読めていない（`departments: null`）ときは、部署のある商談を変更させない（締める側に倒す）。
 */
export const canModifyMeeting = (
  actor: MeetingActor,
  meeting: { createdBy?: string | null; deptId?: string | null },
): boolean => {
  if (actor.canEditOthers) return true;
  if (!meeting.createdBy || meeting.createdBy !== actor.userId) return false;
  if (!meeting.deptId) return true;
  if (!actor.departments) return false;
  if (actor.departments.liveDeptIds && !actor.departments.liveDeptIds.has(meeting.deptId)) return true;
  return actor.departments.viewerDeptIds.includes(meeting.deptId);
};

/** 商談記録の参加者か（PRODUCT_DEV-1024）。記録者本人は参加者に数えない（記録者は `canModifyMeeting` で通る）。 */
export const isMeetingParticipant = (
  meeting: { participantUserIds?: readonly string[] | null },
  userId: string,
): boolean => Boolean(userId) && (meeting.participantUserIds ?? []).includes(userId);

/**
 * 商談記録の**内容を直せるか**（PRODUCT_DEV-1024）。`canModifyMeeting` か、参加者。
 *
 * ⚠ **参加者に通すのは内容の編集だけ。** 削除・ゴミ箱からの復元・ステータス変更・部署や日程調整の
 * 付け替え・振り返りの追記・参加者の付け外しは、今までどおり `canModifyMeeting` で見る。
 * `canModifyMeeting` の判定に参加者を足すと、それを使う削除や一括操作まで参加者に開いてしまう。
 */
export const canEditMeetingContent = (
  actor: MeetingActor,
  meeting: {
    createdBy?: string | null;
    deptId?: string | null;
    participantUserIds?: readonly string[] | null;
  },
): boolean => canModifyMeeting(actor, meeting) || isMeetingParticipant(meeting, actor.userId);

/**
 * 商談の部署を、その部署へ付け替えてよいか（付け替え先の判定。商談を変更できるかは `canModifyMeeting` で別に見る）。
 * 付け替え先は削除されていない部署で、admin 以上ならどれでも、それ以外は自分が所属している部署だけ。
 */
export const canAssignMeetingDept = (
  actor: { canEditOthers: boolean; departments: ViewerDepartments },
  deptId: string,
): boolean =>
  (actor.departments.liveDeptIds?.has(deptId) ?? false) &&
  (actor.canEditOthers || actor.departments.viewerDeptIds.includes(deptId));

/**
 * 記録を作るときに送られた部署のうち、保存してよいもの（本人が所属する削除されていない部署）。
 * それ以外は捨てる（記録そのものは保存する。録音を失わないため）。
 */
export const acceptedMeetingDeptId = (
  requested: string | undefined,
  departments: ViewerDepartments,
): string | undefined =>
  requested && departments.viewerDeptIds.includes(requested) ? requested : undefined;

// ---- 辞書の言葉の変更（PRODUCT_DEV-1108） ----

/**
 * 辞書に言葉を足せるか（PRODUCT_DEV-1108）。見るのは誰でもできる。
 * 閲覧者（`viewer`・閲覧のみの役割）は足せない（2026-10-13 ユーザ判断）。
 */
export const canAddDictionaryEntry = (source: SystemRoleSource): boolean =>
  source.role !== "viewer";

/**
 * 辞書の言葉を変更（直す・無効化・有効に戻す・完全に削除）できるか（PRODUCT_DEV-1108）。
 * api（`lambda/admin/dictionaries`）と web（辞書の一覧・詳細）が同じ判定を使う。
 *
 * - 変更は**足した本人**か、`tenantData.manage`（admin 以上）
 * - 本人でも、足せない人（`canAddDictionaryEntry` が false＝閲覧者）は変更できない
 * - 業務軸の manager は含めない。辞書はテナントで1つを共有していて部署ごとではないので、
 *   どこかの部署の manager にテナント全体の言葉を消させない（2026-10-13 ユーザ判断）
 * - `createdBy` の無い言葉は本人と一致しない扱い（admin だけが変更できる）
 */
export const canModifyDictionaryEntry = (
  actor: { userId: string; canManageTenantData: boolean; canAddEntries: boolean },
  entry: { createdBy?: string | null },
): boolean =>
  actor.canManageTenantData ||
  (actor.canAddEntries && Boolean(entry.createdBy) && entry.createdBy === actor.userId);
